import { useCallback, useState } from "react";
import { toast } from "sonner";
import { useUser } from "../context/UserContext";
import {
  lockResource,
  unlockResource,
  isLockedError,
} from "../service/apiService";
import type { Lockable, LockFileType, LockResourceType } from "../types";

/**
 * Admin locking for a single BrainBank, Virtual Assistant or File.
 *
 * A locked resource is exclusive to the admin who locked it: the API hides it
 * from everybody else's lists and rejects their requests with 423. Because of
 * that, a row a non-locking user can see is a row that is not locked -- so in
 * practice `isLocked` here is only ever true for the locker's own rows.
 */
export interface UseResourceLockOptions {
  resourceType: LockResourceType;
  resourceId?: string;
  /** The row itself, or just its lock fields. */
  resource?: Lockable | null;
  /**
   * Who created the resource: `created_by` for a BrainBank, `user_id` for a VA,
   * `uploaded_by_id` for a file. An admin may only lock their own resources, so
   * the control is hidden when this is not them.
   */
  ownerId?: string | null;
  /** Required when resourceType is "file". */
  fileType?: LockFileType;
  /** Called after a successful lock/unlock so the caller can refetch. */
  onChange?: () => void;
}

export interface UseResourceLockResult {
  isLocked: boolean;
  /** Whether to render a lock control at all -- admins only. */
  canLock: boolean;
  busy: boolean;
  /** `targetId` overrides resourceId — pass the id captured at click time. */
  lock: (targetId?: string) => Promise<void>;
  unlock: (targetId?: string) => Promise<void>;
  toggle: (targetId?: string) => Promise<void>;
  /** e.g. "Locked on 10 Aug 2026", for a tooltip. */
  lockedLabel: string | null;
}

const NOUNS: Record<LockResourceType, string> = {
  brainbank: "BrainBank",
  va: "Virtual Assistant",
  file: "File",
};

/** Case-insensitive id match; mirrors normalise_user() on the API side. */
export const sameUser = (a?: string | null, b?: string | null) => {
  const left = (a || "").trim().toLowerCase();
  const right = (b || "").trim().toLowerCase();
  return Boolean(left) && left === right;
};

export const useResourceLock = ({
  resourceType,
  resourceId,
  resource,
  ownerId,
  fileType,
  onChange,
}: UseResourceLockOptions): UseResourceLockResult => {
  const { userId, userDetails } = useUser();
  const [busy, setBusy] = useState(false);

  // Two conditions, both mirroring the API:
  //   1. admin role -- note role === "admin", not the role !== "user" shorthand
  //      some list views use for "elevated", because locking is specifically an
  //      admin capability;
  //   2. the admin created this resource -- an admin can see every VA and
  //      BrainBank in the org, so without this the control would appear on rows
  //      the API will reject with 403.
  // Cosmetic only; the API enforces both independently and is what actually holds.
  const me = userId || userDetails?.id;
  const canLock = userDetails?.role === "admin" && sameUser(ownerId, me);
  const isLocked = Boolean(resource?.locked_by);

  const lockedLabel = (() => {
    if (!isLocked) return null;
    const at = resource?.locked_at;
    if (!at) return "Locked";
    const parsed = new Date(at);
    return Number.isNaN(parsed.getTime())
      ? "Locked"
      : `Locked on ${parsed.toLocaleDateString(undefined, {
          day: "numeric",
          month: "short",
          year: "numeric",
        })}`;
  })();

  const run = useCallback(
    async (next: boolean, targetId?: string) => {
      // targetId is the id captured when the user clicked, which may differ
      // from resourceId if the list refetched or reordered while a
      // confirmation dialog was open. Acting on the captured id is what stops
      // a confirmed lock from landing on whichever row moved into this slot.
      const id = targetId || resourceId;
      if (!id || busy) return;

      const noun = NOUNS[resourceType];
      setBusy(true);
      try {
        if (next) {
          const result = await lockResource(resourceType, id, fileType);
          // Locking clears the shares, assignments and managers that existed at
          // that moment. Say how many people that affected -- they each get a
          // notification, and the admin should know it happened.
          const revoked =
            (result?.revoked_users || 0) + (result?.revoked_managers || 0);
          toast.success(
            revoked
              ? `${noun} locked. ${revoked} ${revoked === 1 ? "person" : "people"} lost access and ${revoked === 1 ? "was" : "were"} notified.`
              : `${noun} locked. Only you can access it now.`
          );
        } else {
          await unlockResource(resourceType, id, fileType);
          toast.success(`${noun} unlocked.`);
        }
        onChange?.();
      } catch (error: any) {
        const detail = error?.response?.data?.detail;
        toast.error(
          detail || `Failed to ${next ? "lock" : "unlock"} this ${noun}.`
        );
        // A 423 means someone else's lock landed first, so what is on screen
        // is already stale -- refetch rather than leave a button that will
        // keep failing.
        if (isLockedError(error)) onChange?.();
      } finally {
        setBusy(false);
      }
    },
    [resourceType, resourceId, fileType, onChange, busy]
  );

  const lock = useCallback((targetId?: string) => run(true, targetId), [run]);
  const unlock = useCallback((targetId?: string) => run(false, targetId), [run]);
  const toggle = useCallback(
    (targetId?: string) => run(!isLocked, targetId),
    [run, isLocked]
  );

  return { isLocked, canLock, busy, lock, unlock, toggle, lockedLabel };
};

export default useResourceLock;
